import { useEffect, useState } from 'react';

import DatePicker from 'react-datepicker';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { CUSTOMER_REQUESTS_FILTERS } from 'constants/customer-request.constants';
import { DateFormates } from 'constants/date.constants';
import { useCustomerRequestsDefaultOptions } from 'hooks/useCustomerRequestsDefaultOptions';
import { useQueryParams } from 'hooks/useQueryParams';
import { useStartEndQuery } from 'hooks/useStartEndQuery';
import { CustomerRequestsFiltersInput } from 'interfaces/customer-requests.interfaces';
import { QueryParams } from 'interfaces/query.interfaces';
import { ButtonVariants } from 'interfaces/shared/button.interfaces';
import { Button } from 'shared-components/Button';
import { Card } from 'shared-components/Card';
import { FilterSelects } from 'shared-components/FilterSelects';
import { FormLabel } from 'shared-components/FormLabel';
import { Input } from 'shared-components/Input';
import { formatToDefaultDate } from 'utils/date.utils';

export const CustomerRequestsFilters = () => {
  const { handleSubmit, register, control, reset } = useForm<CustomerRequestsFiltersInput>();

  const [searchParams, setSearchParams] = useSearchParams();

  const { formatMessage } = useIntl();

  const filterSelects = useCustomerRequestsDefaultOptions();

  const { startDate, endDate, setStartDate, setEndDate } = useStartEndQuery();
  const [queryParams, setQueryParams] = useState<QueryParams>({});

  const onSubmit: SubmitHandler<CustomerRequestsFiltersInput> = (data) => {
    const country = data.countrySelect?.value as number;
    const responsible = data.responsibleSelect?.value as number;
    const requestSource = data.requestSourceSelect?.value as number;
    const product = data.productSelect?.value as number;
    const customerStatus = data.customerStatusSelect?.value as number;
    const requestStatus = data.requestStatusSelect?.value as number;

    const [startDate, endDate] = data?.dateRange
      ? (data.dateRange as unknown as [Date | undefined, Date | undefined])
      : [];

    const queryParams = {
      requestSource,
      responsible,
      requestStatus,
      product,
      customerStatus,
      country: `${country ?? ''}`,
      startDate: startDate ? `${formatToDefaultDate(startDate)}` : undefined,
      endDate: endDate ? `${formatToDefaultDate(endDate)}` : undefined,
      search: data?.search,
    };

    setQueryParams(queryParams);
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleResetFilters = () => {
    setSearchParams({});

    reset({
      search: '',
      countrySelect: { value: '', label: '' },
      responsibleSelect: { value: '', label: '' },
      productSelect: { value: '', label: '' },
      customerStatusSelect: { value: '', label: '' },
      requestStatusSelect: { value: '', label: '' },
      requestSourceSelect: { value: '', label: '' },
      dateRange: '',
    });
  };

  useEffect(() => {
    reset({
      search: searchParams.get('search') ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSelects]);

  useQueryParams(queryParams);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card className='flex flex-col gap-4'>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 items-end'>
          {filterSelects && (
            <FilterSelects filterSelects={filterSelects} data={CUSTOMER_REQUESTS_FILTERS} control={control} />
          )}
          <div className='w-full flex-1'>
            <FormLabel htmlFor='dateRange'>
              <FormattedMessage id='date_range' />
            </FormLabel>
            <div className='mt-2'>
              <Controller
                control={control}
                name='dateRange'
                render={({ field: { onChange, ...field } }) => (
                  <DatePicker
                    {...field}
                    showIcon
                    onChange={(dates) => {
                      handleDateChange(dates);
                      onChange(dates);
                    }}
                    startDate={startDate}
                    autoComplete='off'
                    endDate={endDate}
                    dateFormat={DateFormates.defaultDate}
                    wrapperClassName='w-full'
                    className='customer flex-1 h-full disabled:border-0'
                    selectsRange
                  />
                )}
              />
            </div>
          </div>
          <Input
            {...register('search')}
            label='search'
            className='flex-1'
            autoComplete='off'
            placeholder={formatMessage({ id: 'search' })}
          />
        </div>
        <div className='flex gap-4'>
          <Button variant={ButtonVariants.CANCEL_OUTLINED} onClick={handleResetFilters}>
            <FormattedMessage id='reset_filters' />
          </Button>
          <Button type='submit'>
            <FormattedMessage id='filter' />
          </Button>
        </div>
      </Card>
    </form>
  );
};
